import { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { BroData, isSocialBro } from '@/lib/broCode';
import CompletionHeader from './CompletionComponents/CompletionHeader';
import ShadowBroContent from './CompletionComponents/ShadowBroContent';
import InitiatedBroContent from './CompletionComponents/InitiatedBroContent';
import SocialBroContent from './CompletionComponents/SocialBroContent';
import RerollDialog from './CompletionComponents/RerollDialog';
import BrometheusDialog from './CompletionComponents/BrometheusDialog';
import { useInitiationHandlers } from './InitiationHandlers';
import { generateTwitterShareUrl } from '@/lib/broScore';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface CompletionStepProps {
  completionRank: string;
  broData: BroData;
  pancakeswapUrl: string;
  mobileDetected: boolean;
  onBroDataUpdate: (newData: BroData) => void;
}

const CompletionStep = ({ 
  completionRank, 
  broData, 
  pancakeswapUrl, 
  mobileDetected,
  onBroDataUpdate 
}: CompletionStepProps) => {
  const { address } = useAccount();
  const [isRerollDialogOpen, setIsRerollDialogOpen] = useState(false);
  const [isBrometheusDialogOpen, setIsBrometheusDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentBroData, setCurrentBroData] = useState<BroData>(broData);

  const {
    handleRerollWithXp,
    handleRerollWithTokens,
    handleRerollWithLiquidity,
    handleBrometheusUpgrade
  } = useInitiationHandlers(mobileDetected);

  // Update local state when broData prop changes
  useEffect(() => {
    setCurrentBroData(broData);
  }, [broData]);

  // Function to refresh BroData from database
  const refreshBroData = async () => {
    if (!address) return;
    
    try {
      const { data, error } = await supabase
        .from('initiated_bros')
        .select('*')
        .eq('wallet_address', address.toLowerCase())
        .single();

      if (data && !error) {
        setCurrentBroData(data as BroData);
        onBroDataUpdate(data as BroData);
        
        // Store BroCode in localStorage for authentication
        if (data.brocode) {
          localStorage.setItem('brocode', data.brocode);
          localStorage.setItem('user_brocode', data.brocode);
          console.log(`✅ [STORAGE] BroCode updated: ${data.brocode}`);
        }
        
        console.log('BroData refreshed:', data);
      }
    } catch (error) {
      console.error('Error refreshing BroData:', error);
    }
  };

  const handleOpenBrometheusDialog = () => {
    console.log('Opening BROMETHEUS dialog...');
    setIsBrometheusDialogOpen(true);
  };

  const handleRerollAction = async (actionType: 'xp' | 'tokens' | 'liquidity') => {
    if (!address) {
      toast.error("Wallet not connected");
      return;
    }

    setIsLoading(true);
    let result;

    try {
      switch (actionType) {
        case 'xp':
          result = await handleRerollWithXp(address);
          break;
        case 'tokens':
          result = await handleRerollWithTokens(address);
          break;
        case 'liquidity':
          result = await handleRerollWithLiquidity(address);
          break;
      }

      if (result?.success && result.brocode) {
        // Refresh BroData from database to get the latest state
        await refreshBroData();
        setIsRerollDialogOpen(false);
        toast.success("BroCode rerolled successfully!");
      }
    } catch (error) {
      console.error('Error in reroll action:', error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBrometheusAction = async (actionType: 'xp' | 'tokens' | 'liquidity') => {
    if (!address) {
      toast.error("Wallet not connected");
      return;
    }

    setIsLoading(true);
    let result;

    try {
      const useXP = actionType === 'xp';
      const useLiquidity = actionType === 'liquidity';
      
      result = await handleBrometheusUpgrade(address, useXP, useLiquidity);

      if (result?.success && result.brocode) {
        // Refresh BroData from database to get the latest state
        await refreshBroData();
        setIsBrometheusDialogOpen(false);
      }
    } catch (error) {
      console.error('Error in BROMETHEUS action:', error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const twitterShareUrl = generateTwitterShareUrl(currentBroData.brocode);
  const currentRank = currentBroData.rank || completionRank;

  return (
    <div className="text-center">
      <CompletionHeader completionRank={currentRank} />
      
      {currentRank === 'Shadow Bro' ? (
        <ShadowBroContent 
          brocode={currentBroData.brocode}
          pancakeswapUrl={pancakeswapUrl}
        />
      ) : isSocialBro(currentRank) ? (
        <SocialBroContent 
          brocode={currentBroData.brocode}
          twitterHandle={currentBroData.twitter_handle || ''}
          onOpenRerollDialog={() => setIsRerollDialogOpen(true)}
          onOpenBrometheusDialog={handleOpenBrometheusDialog}
          twitterShareUrl={twitterShareUrl}
          walletAddress={address || ''}
        />
      ) : (
        <InitiatedBroContent 
          brocode={currentBroData.brocode}
          onOpenRerollDialog={() => setIsRerollDialogOpen(true)}
          onOpenBrometheusDialog={handleOpenBrometheusDialog}
          twitterShareUrl={twitterShareUrl}
          walletAddress={address || ''}
        />
      )}

      <RerollDialog
        isOpen={isRerollDialogOpen}
        onClose={() => setIsRerollDialogOpen(false)}
        currentBrocode={currentBroData.brocode}
        isLoading={isLoading}
        broData={currentBroData}
        onRerollWithXp={() => handleRerollAction('xp')}
        onRerollWithTokens={() => handleRerollAction('tokens')}
        onRerollWithLiquidity={() => handleRerollAction('liquidity')}
      />

      <BrometheusDialog
        isOpen={isBrometheusDialogOpen}
        onClose={() => setIsBrometheusDialogOpen(false)}
        currentBrocode={currentBroData.brocode}
        isLoading={isLoading}
        broData={currentBroData}
        brometheusAvailable={true}
        onClaimWithXp={() => handleBrometheusAction('xp')}
        onClaimWithTokens={() => handleBrometheusAction('tokens')}
        onClaimWithLiquidity={() => handleBrometheusAction('liquidity')}
      />
    </div>
  );
};

export default CompletionStep;
